(() => {
  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();
  const splitValues = (value) => String(value || '').split(/\|\||[;,\n]/).map((item) => item.trim()).filter(Boolean);
  const selectionStorageKey = 'solvi:sample-bag:selections';

  class SolviBuildSampleSet {
    constructor(root) {
      if (!root || root.dataset.solviBuilderReady === 'true') return;
      root.dataset.solviBuilderReady = 'true';

      this.root = root;
      this.maxCount = Number.parseInt(root.dataset.maxCount, 10) || 5;
      this.buttonText = root.dataset.buttonText || 'add sample bag to cart';
      this.disabledButtonText = root.dataset.disabledButtonText || `choose ${this.maxCount} scents to continue`;
      this.selected = new Map();
      this.cartSynced = false;
      this.cards = Array.from(root.querySelectorAll('[data-solvi-builder-card]'));
      this.grid = root.querySelector('[data-solvi-builder-grid]');
      this.loadMore = root.querySelector('[data-solvi-builder-load-more]');
      this.nextUrl = this.loadMore?.dataset.nextUrl || '';
      this.loadPromise = null;
      this.filters = Array.from(root.querySelectorAll('[data-solvi-builder-filter]'));
      this.progress = root.querySelector('[data-solvi-builder-progress]');
      this.trayCount = root.querySelector('[data-solvi-builder-tray-count]');
      this.slots = Array.from(root.querySelectorAll('[data-solvi-builder-slots] > li'));
      this.dots = Array.from(root.querySelectorAll('[data-solvi-builder-dots] > i'));
      this.submit = root.querySelector('[data-solvi-builder-submit]');
      this.form = root.querySelector('[data-solvi-builder-form]');
      this.filterEmpty = root.querySelector('[data-solvi-builder-filter-empty]');
      this.clearFilters = root.querySelector('[data-solvi-builder-clear]');
      this.announcement = root.querySelector('[data-solvi-builder-announcement]');
      this.search = root.querySelector('[data-solvi-builder-search]');
      this.quizStatus = root.querySelector('[data-solvi-builder-quiz-status]');
      this.cartStatus = root.querySelector('[data-solvi-builder-cart-status]');
      this.searchTerm = '';
      this.searchTimer = null;

      this.populateFilters();
      this.bindEvents();
      const isQuizHandoff = new URLSearchParams(window.location.search).get('from') === 'quiz';
      if (!isQuizHandoff) this.restoreSelections();
      this.render();
      this.filterCards();
      this.restoreQuizPicks();
      if (!isQuizHandoff) this.syncSelectionsWithCart();
    }

    populateFilters() {
      this.filters.forEach((filter) => {
        const filterName = filter.dataset.solviBuilderFilter;
        const values = new Map();
        const options = filter.querySelector('[data-solvi-builder-filter-options]');
        const placeholder = filter.dataset.placeholder || 'all';

        if (!options) return;

        this.cards.forEach((card) => {
          splitValues(card.dataset[`filter${filterName.charAt(0).toUpperCase()}${filterName.slice(1)}`]).forEach((label) => {
            const key = normalize(label);
            if (key && !values.has(key)) values.set(key, label);
          });
        });

        const selectedValues = splitValues(filter.dataset.values).map(normalize);
        options.replaceChildren(this.createFilterOption(filter, '', placeholder, selectedValues.length === 0));

        Array.from(values.entries())
          .sort((a, b) => a[1].localeCompare(b[1]))
          .forEach(([value, label]) => {
            options.append(this.createFilterOption(filter, value, label, selectedValues.includes(value)));
          });

        filter.querySelectorAll('[data-solvi-builder-filter-option]').forEach((option) => this.bindFilterOption(filter, option));
      });
    }

    createFilterOption(filter, value, label, selected) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'solvi-build-filter__option';
      option.dataset.solviBuilderFilterOption = '';
      option.dataset.value = value;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(selected));
      option.textContent = label;
      return option;
    }

    bindFilterOption(filter, option) {
      if (option.dataset.solviBuilderBound === 'true') return;
      option.dataset.solviBuilderBound = 'true';
      option.addEventListener('click', () => {
        const value = option.dataset.value || '';
        if (value) {
          option.setAttribute('aria-selected', String(option.getAttribute('aria-selected') !== 'true'));
          this.updateFilterSelection(filter);
        } else {
          this.clearFilter(filter);
        }
        this.filterCards();
      });
    }

    bindCard(card) {
      const variantSelector = card.querySelector('[data-solvi-builder-variant]');
      if (variantSelector && variantSelector.dataset.solviBuilderBound !== 'true') {
        variantSelector.dataset.solviBuilderBound = 'true';
        variantSelector.addEventListener('change', () => this.changeCardVariant(card, variantSelector));
      }

      const button = card.querySelector('[data-solvi-builder-toggle]');
      if (!button || button.dataset.solviBuilderBound === 'true') return;
      button.dataset.solviBuilderBound = 'true';
      button.addEventListener('click', () => this.toggleCard(card));
    }

    changeCardVariant(card, selector) {
      const option = selector.selectedOptions[0];
      if (!option || option.disabled) return;

      const variantId = Number.parseInt(option.value, 10);
      const variantTitle = option.dataset.variantTitle || option.textContent.trim();
      const price = option.dataset.price || '';
      card.dataset.variantId = String(variantId || '');
      card.dataset.variantTitle = variantTitle;
      card.dataset.price = price;
      card.querySelector('[data-solvi-builder-price]')?.replaceChildren(price);

      const productId = card.dataset.productId;
      if (this.selected.has(productId)) {
        const selection = this.selected.get(productId);
        this.selected.set(productId, { ...selection, variantId, variantTitle, price });
        this.saveSelections();
        this.announce(`${selection.title} changed to ${variantTitle}.`);
      }
      this.render();
    }

    bindEvents() {
      this.cards.forEach((card) => this.bindCard(card));

      this.filters.forEach((filter) => {
        const details = filter.querySelector('details');
        const summary = filter.querySelector('summary');

        details?.addEventListener('toggle', () => {
          if (!details.open) return;
          this.filters.forEach((otherFilter) => {
            if (otherFilter === filter) return;
            const otherDetails = otherFilter.querySelector('details');
            if (otherDetails) otherDetails.open = false;
          });
        });

        details?.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          details.open = false;
          summary?.focus();
        });
      });

      this.clearFilters?.addEventListener('click', () => {
        this.filters.forEach((filter) => this.clearFilter(filter));
        this.filterCards();
        this.filters[0]?.querySelector('summary')?.focus();
      });

      this.search?.addEventListener('input', () => {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(async () => {
          this.searchTerm = normalize(this.search.value);
          if (this.searchTerm) await this.loadAllPages();
          this.filterCards();
        }, 180);
      });

      this.loadMore?.addEventListener('click', () => this.loadNextPage());
      window.addEventListener('pageshow', () => this.syncSelectionsWithCart());

      this.root.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-solvi-builder-remove]');
        if (removeButton) {
          this.removeSelection(removeButton.dataset.productId);
          return;
        }
        if (event.target.closest('.solvi-build-filter')) return;
        this.filters.forEach((filter) => {
          const details = filter.querySelector('details');
          if (details) details.open = false;
        });
      });

      this.form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (this.selected.size !== this.maxCount) {
          this.announce(this.disabledButtonText);
          return;
        }
        await this.addSelectionsToCart();
      });
    }

    updateFilterSelection(filter) {
      const options = Array.from(filter.querySelectorAll('[data-solvi-builder-filter-option]'));
      const selectedOptions = options.filter((option) => option.dataset.value && option.getAttribute('aria-selected') === 'true');
      const placeholderOption = options.find((option) => !option.dataset.value);
      const placeholder = filter.dataset.placeholder || 'all';
      const selectedValues = selectedOptions.map((option) => option.dataset.value);
      const currentValue = filter.querySelector('[data-solvi-builder-filter-value]');
      const summary = filter.querySelector('summary');
      let label = placeholder;

      if (selectedOptions.length === 1) label = selectedOptions[0].textContent.trim();
      if (selectedOptions.length > 1) label = `${selectedOptions.length} selected`;

      filter.dataset.values = selectedValues.join('||');
      placeholderOption?.setAttribute('aria-selected', String(selectedOptions.length === 0));

      if (currentValue) currentValue.textContent = label;
      if (summary) summary.setAttribute('aria-label', `${filter.querySelector('.solvi-build-filter__label')?.textContent || 'Filter'}: ${label}`);
    }

    clearFilter(filter) {
      filter.querySelectorAll('[data-solvi-builder-filter-option]').forEach((option) => {
        option.setAttribute('aria-selected', String(!option.dataset.value));
      });
      this.updateFilterSelection(filter);
    }

    toggleCard(card) {
      const id = card.dataset.productId;
      const title = card.dataset.title || 'Selected scent';
      const vendor = card.dataset.vendor || '';
      const variantId = Number.parseInt(card.dataset.variantId, 10);
      const variantTitle = card.dataset.variantTitle || '';
      const price = card.dataset.price || '';
      const handle = card.dataset.productHandle || '';

      if (!variantId) {
        this.announce(`${title} does not have an available sample right now.`);
        return;
      }

      if (this.selected.has(id)) {
        this.selected.delete(id);
        this.announce(`${title} removed. ${this.selected.size} of ${this.maxCount} selected.`);
      } else if (this.selected.size < this.maxCount) {
        this.selected.set(id, { productId: id, title, vendor, variantId, variantTitle, price, handle });
        this.announce(`${title} added. ${this.selected.size} of ${this.maxCount} selected.`);
      } else {
        this.announce(`Your bag already has ${this.maxCount} scents. Remove one before adding another.`);
        return;
      }

      this.saveSelections();
      this.render();
    }

    removeSelection(productId) {
      if (!productId || !this.selected.has(productId)) return;
      const title = this.selected.get(productId)?.title || 'Sample';
      this.selected.delete(productId);
      this.saveSelections();
      this.announce(`${title} removed. ${this.selected.size} of ${this.maxCount} selected.`);
      this.render();
    }

    saveSelections({ cartSynced = false } = {}) {
      this.cartSynced = cartSynced;
      try {
        window.localStorage.setItem(selectionStorageKey, JSON.stringify({
          selections: Array.from(this.selected.values()),
          cartSynced: this.cartSynced
        }));
      } catch (error) {
        // The builder still works when storage is unavailable.
      }
    }

    restoreSelections() {
      let storedValue = [];
      try {
        storedValue = JSON.parse(window.localStorage.getItem(selectionStorageKey) || '[]');
      } catch (error) {
        storedValue = [];
      }

      const savedSelections = Array.isArray(storedValue) ? storedValue : storedValue?.selections;
      this.cartSynced = !Array.isArray(storedValue) && storedValue?.cartSynced === true;
      if (!Array.isArray(savedSelections)) return;
      savedSelections.slice(0, this.maxCount).forEach((selection) => {
        const productId = String(selection?.productId || '');
        const variantId = Number.parseInt(selection?.variantId, 10);
        if (!productId || !variantId || this.selected.has(productId)) return;
        this.selected.set(productId, {
          productId,
          variantId,
          title: String(selection?.title || 'Selected sample'),
          vendor: String(selection?.vendor || ''),
          variantTitle: String(selection?.variantTitle || ''),
          price: String(selection?.price || ''),
          handle: String(selection?.handle || '')
        });
      });
    }

    async syncSelectionsWithCart() {
      if (!this.cartSynced || this.selected.size === 0) return;

      try {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const response = await window.fetch(`${rootUrl}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;

        const cart = await response.json();
        const cartSampleVariantIds = new Set((cart?.items || [])
          .filter((item) => item?.properties?.['_Solvi sample bag'] === 'Yes')
          .map((item) => Number.parseInt(item.variant_id || item.id, 10))
          .filter(Boolean));
        const removedTitles = [];

        this.selected.forEach((selection, productId) => {
          if (cartSampleVariantIds.has(selection.variantId)) return;
          removedTitles.push(selection.title);
          this.selected.delete(productId);
        });

        if (removedTitles.length === 0) return;
        this.saveSelections({ cartSynced: true });
        this.render();
        this.announce(`${removedTitles.join(', ')} removed from your tray to match your cart.`);
      } catch (error) {
        // Keep the saved tray intact if the cart cannot be checked.
      }
    }

    render() {
      const count = this.selected.size;
      const isComplete = count === this.maxCount;
      const selections = Array.from(this.selected.values());

      this.root.classList.toggle('is-complete', isComplete);
      if (this.progress) this.progress.textContent = `${count} / ${this.maxCount} selected`;
      if (this.trayCount) this.trayCount.textContent = `${count} / ${this.maxCount}`;

      this.dots.forEach((dot, index) => dot.classList.toggle('is-filled', index < count));

      this.cards.forEach((card) => {
        const button = card.querySelector('[data-solvi-builder-toggle]');
        const label = card.querySelector('[data-solvi-builder-toggle-label]');
        const selection = this.selected.get(card.dataset.productId);
        const selected = Boolean(selection);
        const variantSelector = card.querySelector('[data-solvi-builder-variant]');
        if (selection && variantSelector) {
          const savedOption = Array.from(variantSelector.options).find((option) => (
            Number.parseInt(option.value, 10) === selection.variantId && !option.disabled
          ));
          if (savedOption) {
            variantSelector.value = savedOption.value;
            card.dataset.variantId = savedOption.value;
            card.dataset.variantTitle = savedOption.dataset.variantTitle || '';
            card.dataset.price = savedOption.dataset.price || '';
            card.querySelector('[data-solvi-builder-price]')?.replaceChildren(savedOption.dataset.price || '');
            selection.variantTitle = savedOption.dataset.variantTitle || selection.variantTitle || '';
            selection.price = savedOption.dataset.price || selection.price || '';
          }
        }
        const unavailable = !Number.parseInt(card.dataset.variantId, 10);
        const fullAndUnavailable = isComplete && !selected;

        card.classList.toggle('is-selected', selected);
        card.classList.toggle('is-unavailable', unavailable);
        button?.setAttribute('aria-pressed', String(selected));
        if (button) {
          button.disabled = unavailable || fullAndUnavailable;
          button.setAttribute('aria-label', `${selected ? 'Remove' : 'Add'} ${card.dataset.title || 'scent'} ${selected ? 'from' : 'to'} your sample bag`);
        }
        if (label) label.textContent = unavailable ? 'unavailable' : selected ? 'added ✓' : 'add to bag +';
      });

      this.slots.forEach((slot, index) => {
        const selection = selections[index];
        slot.replaceChildren();
        slot.classList.toggle('is-filled', Boolean(selection));
        slot.dataset.productId = selection?.productId || '';

        const name = document.createElement('span');
        name.textContent = selection ? selection.title : `choose scent ${index + 1}`;
        slot.append(name);

        const details = selection
          ? [selection.vendor, selection.variantTitle, selection.price].filter(Boolean).join(' · ')
          : '';
        if (details) {
          const vendor = document.createElement('small');
          vendor.textContent = details;
          slot.append(vendor);
        }

        if (selection) {
          const removeButton = document.createElement('button');
          removeButton.type = 'button';
          removeButton.className = 'solvi-build-tray__remove';
          removeButton.dataset.solviBuilderRemove = '';
          removeButton.dataset.productId = selection.productId;
          removeButton.setAttribute('aria-label', `Remove ${selection.title} from your sample bag`);
          removeButton.textContent = '×';
          slot.append(removeButton);
        }
      });

      if (this.submit) {
        this.submit.disabled = !isComplete;
        this.submit.textContent = isComplete ? this.buttonText : this.disabledButtonText;
      }
    }

    filterCards() {
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const matchesFilters = this.filters.every((filter) => {
          const selectedValues = splitValues(filter.dataset.values).map(normalize);
          if (selectedValues.length === 0) return true;
          const filterName = filter.dataset.solviBuilderFilter;
          const datasetKey = `filter${filterName.charAt(0).toUpperCase()}${filterName.slice(1)}`;
          const cardValues = splitValues(card.dataset[datasetKey]).map(normalize);
          return selectedValues.some((selectedValue) => cardValues.includes(selectedValue));
        });
        const matchesSearch = !this.searchTerm || normalize(card.dataset.searchText).includes(this.searchTerm);
        const matches = matchesFilters && matchesSearch;

        card.hidden = !matches;
        if (matches) visibleCount += 1;
      });

      if (this.filterEmpty) this.filterEmpty.hidden = visibleCount !== 0;
    }

    announce(message) {
      if (!this.announcement) return;
      this.announcement.textContent = '';
      window.requestAnimationFrame(() => { this.announcement.textContent = message; });
    }

    async restoreQuizPicks() {
      if (new URLSearchParams(window.location.search).get('from') !== 'quiz') return;

      await this.loadAllPages();

      let result;
      try {
        result = JSON.parse(window.localStorage.getItem('solvi:fragrance-quiz:last-result') || 'null');
      } catch (error) {
        result = null;
      }

      const urlPicks = String(new URLSearchParams(window.location.search).get('picks') || '').split(',').map((handle) => handle.trim()).filter(Boolean);
      const storedPicks = Array.isArray(result?.recommendations) ? result.recommendations : [];
      const picks = urlPicks.length ? urlPicks.map((handle) => ({ handle })) : storedPicks;
      let restored = 0;
      picks.forEach((pick) => {
        if (this.selected.size >= this.maxCount) return;
        const card = this.cards.find((item) => normalize(item.dataset.productHandle) === normalize(pick.handle) && Number.parseInt(item.dataset.variantId, 10));
        if (!card || this.selected.has(card.dataset.productId)) return;
        this.selected.set(card.dataset.productId, {
          productId: card.dataset.productId,
          title: card.dataset.title || pick.title,
          vendor: card.dataset.vendor || '',
          variantId: Number.parseInt(card.dataset.variantId, 10),
          variantTitle: card.dataset.variantTitle || '',
          price: card.dataset.price || '',
          handle: card.dataset.productHandle || pick.handle
        });
        restored += 1;
      });
      this.saveSelections();
      this.render();

      if (this.quizStatus) {
        this.quizStatus.hidden = false;
        const remaining = Math.max(0, this.maxCount - restored);
        const moreLabel = remaining === 1 ? '1 more' : `${remaining} more`;
        this.quizStatus.textContent = restored === picks.length && restored > 0
          ? `Your ${restored} quiz picks are ready. Choose ${moreLabel} to complete your bag.`
          : restored > 0
            ? `${restored} of your ${picks.length} matches are available to sample. Pick ${moreLabel} to complete your bag.`
            : `None of your ${picks.length || 3} matches are available to sample right now. Choose ${this.maxCount} scents to complete your bag.`;
      }
    }

    async loadNextPage() {
      if (!this.nextUrl || this.loadPromise) return this.loadPromise;

      const requestUrl = new URL(this.nextUrl, window.location.href);
      const currentView = new URLSearchParams(window.location.search).get('view');
      if (currentView && !requestUrl.searchParams.has('view')) requestUrl.searchParams.set('view', currentView);
      if (this.loadMore) {
        this.loadMore.disabled = true;
        this.loadMore.textContent = 'loading scents…';
      }

      this.loadPromise = window.fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error('Could not load more scents.');
          return response.text();
        })
        .then((html) => {
          const page = new DOMParser().parseFromString(html, 'text/html');
          const incomingRoot = page.getElementById(this.root.id);
          const incomingCards = Array.from(incomingRoot?.querySelectorAll('[data-solvi-builder-card]') || []);
          const knownProductIds = new Set(this.cards.map((card) => card.dataset.productId));

          incomingCards.forEach((card) => {
            if (knownProductIds.has(card.dataset.productId)) return;
            this.grid?.append(card);
            this.cards.push(card);
            this.bindCard(card);
            knownProductIds.add(card.dataset.productId);
          });

          const incomingLoadMore = incomingRoot?.querySelector('[data-solvi-builder-load-more]');
          this.nextUrl = incomingLoadMore?.dataset.nextUrl || '';
          this.populateFilters();
          this.render();
          this.filterCards();
          return true;
        })
        .catch((error) => {
          const message = error?.message || 'Could not load more scents. Please try again.';
          if (this.cartStatus) this.cartStatus.textContent = message;
          this.announce(message);
          return false;
        })
        .finally(() => {
          this.loadPromise = null;
          if (this.loadMore) {
            this.loadMore.hidden = !this.nextUrl;
            this.loadMore.disabled = false;
            this.loadMore.textContent = 'load more scents';
            if (this.nextUrl) this.loadMore.dataset.nextUrl = this.nextUrl;
          }
        });

      return this.loadPromise;
    }

    async loadAllPages() {
      while (this.nextUrl) {
        const loaded = await this.loadNextPage();
        if (!loaded) break;
      }
    }

    async addSelectionsToCart() {
      const selections = Array.from(this.selected.values());
      const items = selections.map((selection) => ({
        id: selection.variantId,
        quantity: 1,
        properties: { '_Solvi sample bag': 'Yes' }
      }));

      if (items.some((item) => !item.id)) {
        this.announce('One of these samples is unavailable. Please replace it and try again.');
        return;
      }

      const availability = await Promise.all(selections.map(async (selection) => {
        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await window.fetch(`${rootUrl}variants/${selection.variantId}.js`, {
            headers: { Accept: 'application/json' }
          });
          const variant = await response.json();
          return { selection, available: response.ok && variant?.available !== false };
        } catch (error) {
          return { selection, available: true };
        }
      }));
      const unavailableSelection = availability.find((item) => !item.available)?.selection;
      if (unavailableSelection) {
        const message = `${unavailableSelection.title} is no longer available as a sample. Remove it and choose another scent.`;
        if (this.cartStatus) this.cartStatus.textContent = message;
        this.announce(message);
        return;
      }

      const originalText = this.submit?.textContent;
      if (this.submit) {
        this.submit.disabled = true;
        this.submit.textContent = 'adding your samples…';
      }
      if (this.cartStatus) this.cartStatus.textContent = '';

      try {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const response = await window.fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.description || payload?.message || 'Could not add this sample bag.');

        if (this.cartStatus) this.cartStatus.textContent = 'Your five samples are in the bag. Discount applied at checkout.';
        this.announce('Your five samples were added to the cart.');
        this.saveSelections({ cartSynced: true });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
      } catch (error) {
        const message = error?.message || 'Something went wrong. Please try again.';
        if (this.cartStatus) this.cartStatus.textContent = message;
        this.announce(message);
      } finally {
        if (this.submit) {
          this.submit.disabled = false;
          this.submit.textContent = originalText || this.buttonText;
        }
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-solvi-build-set]').forEach((root) => new SolviBuildSampleSet(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
